import { axiosInstance } from 'utils/http';
import { billableConfigs } from 'utils/urlConstants';
import { showAlert } from 'redux/actions/AlertActions';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from 'react-query';

// Enum values for BillableSourceType
export const BillableSourceType = {
  BILLABLE_UNKNOWN: 0,
  BILLABLE_RUN: 1,
  BILLABLE_FLOW_RUN: 2,
  BILLABLE_QUESTION: 3, // Template questions
  BILLABLE_FLOW: 4,
  BILLABLE_CR: 5, // Clean Rooms (including templates)
  BILLABLE_OWNER: 6,
  BILLABLE_PARTNER: 7,
  BILLABLE_CRQ: 8,
};

/**
 * Hook to upsert billable config
 * @param {Object} options - React Query options
 * @param {boolean} options.showAlerts - Whether to show success/error alerts (default: false)
 * @returns {Object} Mutation object
 */
export const useUpsertBillableConfig = (options = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showAlerts = false, ...restOptions } = options;

  return useMutation(
    'upsertBillableConfig',
    async (config) => {
      const response = await axiosInstance.post(billableConfigs.upsert, {
        config,
      });
      return response.data;
    },
    {
      onSuccess: (response, variables) => {
        // Invalidate queries that might be affected by this change
        queryClient.invalidateQueries(['billableConfig', variables.sourceID]);

        if (showAlerts) {
          dispatch(showAlert({
            message: 'Billable configuration saved successfully',
            type: 'success',
          }));
        }
      },
      onError: (error) => {
        if (showAlerts) {
          dispatch(showAlert({
            message: error.response?.data?.message || 'Failed to save billable configuration',
            type: 'error',
          }));
        }
      },
      ...restOptions,
    },
  );
};

/**
 * Hook to get billable config
 * @param {string} sourceID - The source ID (run ID, flow run ID, question ID,
 *  flow ID, clean room ID, owner org ID, partner org ID, or CRQ ID)
 * @param {number} sourceType - The source type (BILLABLE_RUN, BILLABLE_FLOW_RUN,
 *  BILLABLE_QUESTION, BILLABLE_FLOW, BILLABLE_CR, BILLABLE_OWNER, BILLABLE_PARTNER,
 *  or BILLABLE_CRQ)
 * @param {boolean} enabled - Whether the query should be enabled
 * @param {Object} options - React Query options
 * @param {boolean} options.showAlerts - Whether to show error alerts (default: false)
 * @returns {Object} Query object
 */
export const useGetBillableConfig = (
  sourceID,
  sourceType,
  enabled = true,
  options = {},
) => {
  const dispatch = useDispatch();
  const { showAlerts = false, ...restOptions } = options;

  return useQuery(
    ['billableConfig', sourceID, sourceType],
    async () => {
      const response = await axiosInstance.get(billableConfigs.get, {
        params: {
          sourceID,
          sourceType,
        },
      });
      return response.data?.config || null;
    },
    {
      enabled: enabled && !!sourceID && sourceType !== undefined,
      refetchOnWindowFocus: false,
      onError: (error) => {
        // Only show error if it's not a 404 (not found is expected for non-configured items)
        if (showAlerts && error.response?.status !== 404) {
          dispatch(showAlert({
            message: error.response?.data?.message || 'Failed to fetch billable configuration',
            type: 'error',
          }));
        }
      },
      ...restOptions,
    },
  );
};

/**
 * Hook to bulk delete billable configurations
 * @param {string[]} billingConfigIDs - Array of billing config IDs to delete
 * @returns {Object} Mutation object with mutate, mutateAsync, isLoading, isSuccess, isError
 */
export const useBulkDeleteBillableConfig = () => {
  const dispatch = useDispatch();

  return useMutation(
    (billingConfigIDs) => axiosInstance.post(billableConfigs.bulkDelete, {
      billingConfigIDs,
    }),
    {
      onSuccess: () => {
        dispatch(showAlert({
          message: 'Billable configurations deleted successfully',
          type: 'success',
        }));
      },
      onError: (error) => {
        dispatch(showAlert({
          message: error.response?.data?.message || 'Failed to delete billable configurations',
          type: 'error',
        }));
      },
    },
  );
};
